import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SearchListingsResponse } from '@generated/search-bff';

import { defaultSettingsByLang } from '@/utils/settings';
import { useExposePageProps } from '@/hooks/useExposePageProps';

import { useListing } from './useListing';
import { getProjectUnitsQueryKey, MAX_PROJECT_UNITS_COUNT, INITIAL_PROJECT_UNITS_COUNT } from './queryKeys';
import { useSettings } from '../useSettings';
import { useSearchApi } from '../useSearchApi';
import { useExposeLanguageStore } from '../useExposeLanguageStore';

export const useProjectUnits = ({ shouldShowAllUnits = false }: { shouldShowAllUnits?: boolean } = {}) => {
  const searchApi = useSearchApi();
  const { listingId, pathLanguage } = useExposePageProps();
  const queryClient = useQueryClient();
  const { currency, measurementSystem, language } = useSettings();
  const {
    data: { listing },
  } = useListing();

  const exposeLanguage = useExposeLanguageStore((state) => state.exposeLanguage ?? language);

  const pageSize = shouldShowAllUnits ? MAX_PROJECT_UNITS_COUNT : INITIAL_PROJECT_UNITS_COUNT;
  return useQuery({
    queryKey: getProjectUnitsQueryKey({ listingId, language: exposeLanguage, currency, measurementSystem, pageSize }),
    queryFn: async () => {
      if (!listing) {
        throw new Error('Missing listing');
      }

      const projectUnitsResponse = await searchApi.searchListings(
        {
          parentId: listing.id,
          propertyMarketingType: [listing.propertyMarketingType],
          businessArea: [listing.businessArea],
        },
        {
          language: exposeLanguage,
          currency,
          measurementSystem,
          pageSize,
        },
      );
      if (projectUnitsResponse.status === 'error') {
        throw projectUnitsResponse.error;
      }

      return projectUnitsResponse.data;
    },
    refetchOnMount: false,
    placeholderData: (previousData) => {
      if (previousData) return previousData;

      // When showing all, fall back to the initial page data while the full fetch loads
      if (shouldShowAllUnits) {
        const initialQueryKey = getProjectUnitsQueryKey({
          listingId,
          language: exposeLanguage,
          currency,
          measurementSystem,
          pageSize: INITIAL_PROJECT_UNITS_COUNT,
        });
        const initialData = queryClient.getQueryData<SearchListingsResponse>(initialQueryKey);
        if (initialData) return initialData;
      }

      // Try to get data from server query key if client settings differ
      const serverQueryKey = getProjectUnitsQueryKey({
        listingId,
        language: pathLanguage,
        ...defaultSettingsByLang[pathLanguage],
        pageSize: INITIAL_PROJECT_UNITS_COUNT,
      });
      return queryClient.getQueryData<SearchListingsResponse>(serverQueryKey);
    },
    enabled: Boolean(listing?.isDevelopmentProject),
  });
};
